import { useEffect, useState } from "react";
import logoSvg from "../../public/images/brand/c-logo-klein.svg?raw";

export type SealState = "open" | "active" | "ending";

interface MarkPath {
  d: string;
  fill: string;
  transform: string;
}

/** The real mark, split into the C and the arrow so each can move on its own. */
const MARK: MarkPath[] = [...logoSvg.matchAll(
  /<path d="([^"]+)" fill="(#[0-9A-Fa-f]+)" transform="translate\(([^)]+)\)"/g,
)].map((match) => ({
  d: match[1],
  fill: match[2].toUpperCase(),
  transform: `translate(${match[3]})`,
}));

const bodyPaths = MARK.filter((path) => path.fill === "#5C8FD4");
const arrowPaths = MARK.filter((path) => path.fill === "#3E56D1" || path.fill === "#354BAA");

/** Same center as the C in the logo file. The rings sit just outside the arrow tip. */
const CX = 184;
const CY = 195;

let drawnOnce = false;

export function Seal({
  state = "active",
  size = 200,
  className,
}: {
  seed: string;
  state?: SealState;
  size?: number;
  className?: string;
}) {
  const [animate] = useState(() => !drawnOnce);
  useEffect(() => {
    drawnOnce = true;
  }, []);

  const play = animate && state === "active";

  return (
    <svg
      className={["seal", `seal--${state}`, play ? "seal--draw" : "", className ?? ""].filter(Boolean).join(" ")}
      viewBox="-92 -81 552 552"
      width={size}
      height={size}
      aria-hidden="true"
    >
      <g className="seal__wave" aria-hidden="true">
        <circle className="seal__orbit" cx={CX} cy={CY} r={222} pathLength={1} />
        <circle className="seal__orbit seal__orbit--outer" cx={CX} cy={CY} r={242} pathLength={1} />
        <circle className="seal__comet" cx={CX} cy={CY} r={242} pathLength={1} />
        <circle className="seal__comet seal__comet--echo" cx={CX} cy={CY} r={222} pathLength={1} />
      </g>
      <svg className="seal__body" x={18} y={36} width={328} height={314} viewBox="18 36 328 314">
        {bodyPaths.map((path) => (
          <path key={path.fill} d={path.d} fill={path.fill} transform={path.transform} />
        ))}
      </svg>
      <g className="seal__arrow">
        {arrowPaths.map((path) => (
          <path key={path.fill} d={path.d} fill={path.fill} transform={path.transform} />
        ))}
      </g>
    </svg>
  );
}
